import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, List, ListItem, ResponsiveValue } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
// import elasticlunr, { Index } from 'elasticlunr';
import Fuse from 'fuse.js';
import { useSettings } from '../../stateManagement/SettingsContext';
import { InputWithIcon } from '../molecules/InputWithIcon';
import { appInsights } from '../../appInsights';
// import { trackEvent } from '../../Analytics/ga';
import { NewsArticle } from './NewsSection';
import { generateSlug } from '../../utils';

// Define the structure of articles
interface Article {
  section_id: string;
  section_name: string;
  id: string;
  title: string;
  body: string;
  stags: string[];
  publish_id: string;
}

// Props for the SearchBar component
export interface SearchBarProps {
  searchBarBgColor?: string;
  searchBarTextColor?: string;
  placeholderText?: string;
  placeholderColor?: string;
  placeholderFontSize?: ResponsiveValue<string>;
  placeholderFontFamily?: string;
  placeholderFontWeight?: string;
  searchIconSize?: string;
  iconColor?: string;
  searchBarPx?: ResponsiveValue<string>;
  isFullScreen?: boolean;
  searchBarDropDownTextColor?: string;
  isPreview?: boolean;
}
export const SearchBar: React.FC<SearchBarProps> = ({
  searchBarBgColor = '#ffffff',
  searchBarTextColor = 'white',
  searchIconSize = '100%',
  placeholderFontSize,
  placeholderFontFamily,
  iconColor,
  placeholderText,
  placeholderColor = 'white',
  placeholderFontWeight,
  searchBarPx = { base: '2vw', md: '1vw', lg: '15vw' },
  isFullScreen = true,
  searchBarDropDownTextColor,
  isPreview = false,
}) => {
  const { faqs, language, appId, newsArticles } = useSettings();
  const [query, setQuery] = useState<string>('');

  // --- discriminated union types ---
  type ArticleItem = Article & { type: 'article' };
  type NewsItem = NewsArticle & {
    type: 'news';
    stags?: string[];
    section_name?: string;
    section_id?: string;
  };
  type CombinedArticle = ArticleItem | NewsItem;

  const [results, setResults] = useState<CombinedArticle[]>([]);
  const navigate = useNavigate();

  const filteredNewsArticles = (newsArticles || [])
    .map((article: any) => ({ ...article, enable: article.enable === 'true' }))
    .filter((article: any) => article.enable);

  // Extract plain text from HTML strings
  const extractTextFromHTML = (htmlString: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    return (doc.body.textContent || '').toLowerCase().trim();
  };

  // Filter articles based on app and language
  const filteredArticles = useMemo(
    () =>
      faqs?.filter(
        (article: any) =>
          article.translations?.[language] &&
          article.app_id === appId &&
          article['published?'] === true,
      ) || [],
    [faqs, language, appId],
  );

  // Normalize articles
  const normalizedArticles: Article[] = useMemo(() => {
    return filteredArticles.map((article: any) => ({
      id: article.id,
      title: article.translations[language]?.title?.toLowerCase() || '',
      body: extractTextFromHTML(article.translations[language]?.body || ''),
      stags: article.translations[language]?.stags || [],
      publish_id: article.publish_id,
      section_name: article.section_translations?.[language]?.title || '',
      section_id: article.section_id,
      // include other Article required props if your Article type requires them (imageUrl, pin, etc.)
    }));
  }, [filteredArticles, language]);

  const normalizedNewsArticles: NewsArticle[] = useMemo(() => {
    return filteredNewsArticles.map((article: any) => ({
      id: article.id,
      title: (article.title || '').toLowerCase(),
      body: article.content || '',
      createdDate: article.createdDate || new Date().toISOString().split('T')[0],
      updatedDate: article.updatedDate || new Date().toISOString().split('T')[0],
      publishedDate: article.publishedDate || new Date().toISOString().split('T')[0],
      // include other NewsArticle props if needed (url, image etc.)
    }));
  }, [filteredNewsArticles]);

  // Combine both sets and normalize keys for consistency
  const allArticles = useMemo(() => {
    return [
      ...(normalizedArticles.map(article => ({
        ...article,
        type: 'article' as const,
      })) as ArticleItem[]),
      ...(normalizedNewsArticles.map(article => ({
        ...article,
        type: 'news' as const,
        stags: [], // ensure key exists for Fuse indexing
        section_name: 'News',
        section_id: 'news',
      })) as NewsItem[]),
    ];
  }, [normalizedArticles, normalizedNewsArticles]);

  // Fuse instance
  const fuse = useMemo(() => {
    return new Fuse(allArticles, {
      keys: [
        { name: 'title', weight: 0.6 },
        { name: 'body', weight: 0.3 },
        { name: 'stags', weight: 0.1 },
      ],
      includeScore: true,
      includeMatches: true,
      threshold: 0.5,
      minMatchCharLength: 2,
      ignoreLocation: true,
    });
  }, [allArticles]);

  // Handle search input
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    setQuery(searchValue);

    if (!searchValue.trim()) {
      setResults([]);
      return;
    }

    // trackEvent('search_performed', { search_term: searchValue });

    const searchResults = fuse.search(searchValue);

    // Sort by score (lower is better) and map to items
    const sortedResults = searchResults
      .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
      .map(res => res.item as CombinedArticle);

    setResults(sortedResults);
  };

  // Unified result click handler — branch by type
  const handleResultClick = (item: CombinedArticle) => {
    // clear UI
    setResults([]);
    setQuery('');

    if (item.type === 'article') {
      // Narrowed as ArticleItem
      const art = item as ArticleItem;
      const slug = (art.title || '').toLowerCase().replace(/\s+/g, '-');

      // Navigate to article route (use your existing path structure)
      const publishId = art.publish_id ?? art.id ?? '';
      navigate(`/article/${language}/faq/${publishId}-${slug}`);

      // Telemetry
      appInsights.trackEvent({
        name: 'landingPage',
        properties: {
          buttonName: 'relatedArticleTile',
          userName: 'anonymous',
          id: art.id,
          publishId,
          articleTitle: art.title,
          category: art.section_name,
          searchQuery: query,
          articleId: art.id,
          sectionId: art.section_id,
          language,
        },
      });

      return;
    }

    // News branch
    const news = item as NewsItem;
    // const newsSlug = (news.title || '').toLowerCase().replace(/\s+/g, '-');

    // Example news route — adjust to your real route pattern
    const slug = generateSlug(news.title);
    if (item.type === 'news') {
      // navigate(`/news/${language}/${news.id}-${newsSlug}`);
      navigate(`/news/${news.id}-${slug}`);
    }

    // Telemetry for news
    appInsights.trackEvent({
      name: 'landingPage',
      properties: {
        buttonName: 'relatedNewsTile',
        userName: 'anonymous',
        id: news.id,
        newsTitle: news.title,
        searchQuery: query,
        language,
        date: (news as any).date,
      },
    });
  };

  const resultClickHref = (item: CombinedArticle): string => {
    if (item.type === 'article') {
      const art = item as ArticleItem;
      const slug = (art.title || '').toLowerCase().replace(/\s+/g, '-');
      const publishId = art.publish_id ?? art.id ?? '';
      return `/article/${language}/faq/${publishId}-${slug}`;
    }
    const news = item as NewsItem;
    const slug = generateSlug(news.title);
    if (item.type === 'news') {
      return `/news/${news.id}-${slug}`;
    }
    return '#';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('#inputwithicon') && !target.closest('#searchdropdown')) {
        setResults([]);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const capitalizeFirstLetter = (text: string) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : '';

  const listItemCommonProps = {
    textAlign: 'left' as const,
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    bg: searchBarBgColor,
    color: searchBarDropDownTextColor,
    hover: {
      transform: 'scale(1.01)',
      transition: 'transform 0.2s ease-in-out',
    },
    borderRadius: 'sm',
    borderBottom: '2px solid #f1f2f4',
  };

  return (
    <Box
      overflow="visible"
      position="relative"
      px={searchBarPx}
      mx={{ base: 4, lg: isFullScreen ? 0 : 2 }}
      sx={{
        '@media (orientation: landscape) and (max-width: 932px)': {
          '& input': {
            height: '48px',
            minHeight: '40px',
          },
        },
      }}
    >
      {/* Input Field */}
      <Box width="100%" position="relative">
        <Box bg="rgba(0,0,0,0.6)" borderRadius="0">
          <InputWithIcon
            onChange={handleSearchChange}
            placeholder={placeholderText}
            placeholderColor={placeholderColor}
            placeholderFontSize={placeholderFontSize}
            placeholderFontFamily={placeholderFontFamily}
            placeholderFontWeight={placeholderFontWeight}
            InputIcon={SearchIcon}
            // Switched base and md to px values for better stability
            height={{ base: '48px', md: '50px', lg: '64px' }}
            iconSize={searchIconSize}
            iconColor={iconColor}
            value={query}
            searchBarTextColor={searchBarTextColor}
            searchBarBgColor="transparent"
            borderColor={iconColor}
            borderRadius="0"
          />
        </Box>

        {/* Dropdown List */}
        {results.length > 0 && (
          <Box boxShadow="lg" id="searchdropdown" position="absolute" width="100%" zIndex="999">
            <List styleType="none" border="2px solid #f1f2f4" boxShadow="2xl">
              {results.slice(0, 6).map(article => {
                const ogFaq = faqs?.find(faq => faq.id === article.id);
                const displayTitle = ogFaq?.translations?.[language]?.title || article.title;

                const content = (
                  <>
                    <strong>{capitalizeFirstLetter(displayTitle)}</strong>
                    <Box fontSize="sm" color="gray.500">
                      {article.section_name}
                    </Box>
                  </>
                );

                if (isPreview) {
                  return (
                    <ListItem key={article.id}>
                      <Box
                        as="button"
                        textAlign={listItemCommonProps.textAlign}
                        display={listItemCommonProps.display}
                        width={listItemCommonProps.width}
                        padding={listItemCommonProps.padding}
                        bg={listItemCommonProps.bg}
                        color={listItemCommonProps.color}
                        _hover={listItemCommonProps.hover}
                        borderRadius={listItemCommonProps.borderRadius}
                        borderBottom={listItemCommonProps.borderBottom}
                        cursor="default"
                      >
                        {content}
                      </Box>
                    </ListItem>
                  );
                }

                return (
                  <ListItem key={article.id}>
                    <a href={resultClickHref(article)}>
                      <Box
                        as="button"
                        onClick={(e: {
                          button: number;
                          metaKey: any;
                          ctrlKey: any;
                          shiftKey: any;
                          altKey: any;
                          preventDefault: () => void;
                        }) => {
                          const isLeftClick = e.button === 0;
                          const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                          // SPA routing on normal click
                          if (isLeftClick && !isModifiedClick) {
                            e.preventDefault();
                            handleResultClick(article);
                          }
                        }}
                        textAlign={listItemCommonProps.textAlign}
                        display={listItemCommonProps.display}
                        width={listItemCommonProps.width}
                        padding={listItemCommonProps.padding}
                        bg={listItemCommonProps.bg}
                        color={listItemCommonProps.color}
                        _hover={listItemCommonProps.hover}
                        borderRadius={listItemCommonProps.borderRadius}
                        borderBottom={listItemCommonProps.borderBottom}
                      >
                        {content}
                      </Box>
                    </a>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Box>
    </Box>
  );
};
